import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Input, 
  Space, 
  Row, 
  Col, 
  Radio,
  Typography,
  message
} from 'antd';
import { 
  SearchOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import { adminRegistrationApi } from '@/services/api';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const { Title } = Typography;
const DEFAULT_PAGE_SIZE = 10;

type StatusFilter = 'all' | 'pending' | 'approved' | 'reapplied';

interface RegistrationData {
  key: string;
  no: number;
  academyName: string;
  representativeName: string;
  businessNumber: string;
  userCount: number;
  // 가입상태: 승인대기, 승인, 반려, 재신청
  registrationStatus: 'pending' | 'approved' | 'rejected' | 'repending';
  applicationDate: string;
}

const RegistrationManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKeyword = searchParams.get('q') || '';
  const statusParam = (searchParams.get('status') || '').toUpperCase();
  const initialStatus: StatusFilter =
    statusParam === 'PENDING' ? 'pending' :
    statusParam === 'APPROVED' ? 'approved' :
    statusParam === 'REAPPLIED' ? 'reapplied' : 'all';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const [searchText, setSearchText] = useState(initialKeyword);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [filteredRegistrations, setFilteredRegistrations] = useState<RegistrationData[]>([]);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // 서버 데이터 로드
  const load = useCallback(async (q?: string, sf?: StatusFilter) => {
    try {
      const curQ = typeof q === 'string' ? q : searchText;
      const curSF = typeof sf === 'string' ? sf : statusFilter;
      const params: any = {};
      if (curQ) {
        params.q = curQ;
      }
      if (curSF === 'pending') {
        params.status = 'PENDING';
      } else if (curSF === 'approved') {
        params.status = 'APPROVED';
      } else if (curSF === 'reapplied') {
        params.reapplied = true;
      }
      const res: any = await adminRegistrationApi.list(params);
      const list = Array.isArray(res?.data) ? res.data : [];
      // RegistrationRequest -> 테이블 데이터로 매핑
      const total = list.length;
      const mapped: RegistrationData[] = list.map((it: any, idx: number) => {
        const isReapplied = Boolean(it.reapplied);
        const baseStatus = ((it.status || 'PENDING') as string).toLowerCase();
        return {
        key: String(it.id),
        no: total - idx, // 역순 번호 표시
        academyName: it.academyName,
        representativeName: it.adminName,
        businessNumber: it.businessRegistrationNumber,
        userCount: it.requestedSeatCount || 0,
          registrationStatus: (isReapplied ? 'repending' : baseStatus) as RegistrationData['registrationStatus'],
        applicationDate: (it.requestedAt || '').split('T')[0] || '',
        };
      });
      setFilteredRegistrations(mapped);
    } catch (e) {
      message.error('가입신청 목록 조회 중 오류가 발생했습니다.');
    }
  }, [searchText, statusFilter]);

  const syncSearchParams = useCallback((nextPage: number, nextSize: number, nextQ: string, nextStatus: StatusFilter) => {
    const params = new URLSearchParams();
    if (nextQ) params.set('q', nextQ);
    if (nextStatus === 'pending') params.set('status', 'PENDING');
    else if (nextStatus === 'approved') params.set('status', 'APPROVED');
    else if (nextStatus === 'reapplied') params.set('status', 'REAPPLIED');
    if (nextPage > 1) params.set('page', String(nextPage));
    if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
    setSearchParams(params);
  }, [setSearchParams]);

  useEffect(() => {
    load(searchText, statusFilter);
  }, [statusFilter, load, searchText]);

  useEffect(() => {
    syncSearchParams(page, pageSize, searchText, statusFilter);
  }, [page, pageSize, searchText, statusFilter, syncSearchParams]);

  const handleSearch = () => {
    load(searchText, statusFilter);
    setPage(1);
  };

  const handleReset = () => {
    const q = '';
    setSearchText(q);
    setStatusFilter('all');
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    load(q, 'all');
  };

  const handleStatusFilterChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setPage(1);
  };

  const registrationColumns: ColumnsType<RegistrationData> = [
    { 
      title: '순번', 
      dataIndex: 'no', 
      key: 'no', 
      width: 80, 
      align: 'center' 
    },
    { 
      title: '학원명', 
      dataIndex: 'academyName', 
      key: 'academyName', 
      width: 200 
    },
    { 
      title: '대표자명', 
      dataIndex: 'representativeName', 
      key: 'representativeName', 
      width: 120 
    },
    { 
      title: '사업자등록번호', 
      dataIndex: 'businessNumber', 
      key: 'businessNumber', 
      width: 150 
    },
    { 
      title: '사용인원', 
      dataIndex: 'userCount', 
      key: 'userCount', 
      width: 100,
      align: 'center',
      render: (count: number) => `${count}명`
    },
    { 
      title: '가입상태', 
      dataIndex: 'registrationStatus', 
      key: 'registrationStatus', 
      width: 120,
      render: (_: any, row: any) => {
        const status = (row.registrationStatus || 'pending').toLowerCase();
        const map: any = {
          pending: { text: '승인대기', color: '#fa8c16' },
          approved: { text: '승인', color: '#52c41a' },
          rejected: { text: '반려', color: '#f5222d' },
          repending: { text: '재신청', color: '#1890ff' },
        };
        const cfg = map[status] || map.pending;
        return <span style={{ color: cfg.color, fontWeight: 'bold' }}>{cfg.text}</span>;
      }
    },
    { 
      title: '가입신청일', 
      dataIndex: 'applicationDate', 
      key: 'applicationDate', 
      width: 120 
    }
  ];

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          가입신청 관리
        </Title>

        {/* 검색 및 필터 영역 */}
        <Row gutter={16} align="middle" style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Input
              placeholder="학원명, 대표자명, 대표자ID, 사업자등록번호로 검색"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                검색
              </Button>
              <Button onClick={handleReset}>
                초기화
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 상태 필터 */}
        <Row style={{ marginBottom: '16px' }}>
          <Col>
            <Radio.Group 
              value={statusFilter} 
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              style={{ marginBottom: '16px' }}
            >
              <Radio.Button value="all">전체</Radio.Button>
              <Radio.Button value="pending">승인대기</Radio.Button>
              <Radio.Button value="approved">승인완료</Radio.Button>
              <Radio.Button value="reapplied">재신청</Radio.Button>
            </Radio.Group>
          </Col>
        </Row>

        {/* 테이블 */}
        <Table
          columns={registrationColumns}
          dataSource={filteredRegistrations}
          pagination={{
            total: filteredRegistrations.length,
            current: page,
            pageSize: pageSize,
            showTotal: (total, range) => {
              if (!total) return `0-0 / 총 0개`;
              const start = Math.max(total - range[1] + 1, 1);
              const end = Math.max(total - range[0] + 1, 1);
              return `${start}-${end} / 총 ${total}개`;
            },
            showSizeChanger: true,
            showQuickJumper: true,
            onChange: (p, ps) => {
              setPage(p || 1);
              setPageSize(ps || DEFAULT_PAGE_SIZE);
            },
          }}
          scroll={{ x: 900 }}
          onRow={(record) => ({
            onClick: () => navigate(
              `/admin/registration/detail/${record.key}`,
              { state: { from: `${location.pathname}${location.search}` } },
            ),
            style: { cursor: 'pointer' }
          })}
        />
      </div>
    </AdminLayout>
  );
};

export default RegistrationManagement; 